/**
 * ReviewLayer — DOM-level enhancements for the draft preview.
 *
 * Attaches to `[data-testid=cms-body]` (the CmsPage body container) and:
 *   1. Adds a "Comment" pill next to every <h2> and <h3> heading.
 *   2. Detects text selections inside the body and shows a floating
 *      "Comment on selection" button anchored to the selection rect.
 *   3. Highlights sentences / sections that already have OPEN comments.
 *
 * All UI lives in a fixed overlay so it never mutates the sanitized body HTML
 * itself (that HTML is produced by DOMPurify inside CmsPage and must not be
 * modified after render). We use portals + absolute-positioned overlays.
 *
 * When the reviewer clicks any of these triggers, we push a `pendingAnchor`
 * into the ReviewContext, which the ReviewCommentsPanel picks up to open the
 * "New comment" composer with the excerpt pre-filled.
 */
import { useEffect, useMemo, useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import { useReview } from "./ReviewContext";

/**
 * Compute a stable string anchor from an <h2>/<h3> element's id.
 * The generator gives every heading a kebab-case id so anchors survive
 * cross-render.
 */
function headingAnchor(el) {
  const id = el.getAttribute("id");
  return id ? `${el.tagName.toLowerCase()}:${id}` : null;
}

/**
 * From a `Selection` inside the body, resolve the nearest ancestor heading id
 * so a paragraph-level selection can be tagged with the section it belongs to.
 * Falls back to a hash of the first 60 chars of the selection when no heading
 * is available.
 */
function selectionAnchor(range, root) {
  let node = range.commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  // Walk backwards through siblings of the parent chain looking for an id'd heading
  while (node && node !== root) {
    if ((node.tagName === "H2" || node.tagName === "H3") && node.id) {
      return `sel:${node.id}`;
    }
    // walk previous siblings
    let sib = node.previousElementSibling;
    while (sib) {
      if ((sib.tagName === "H2" || sib.tagName === "H3") && sib.id) {
        return `sel:${sib.id}`;
      }
      sib = sib.previousElementSibling;
    }
    node = node.parentElement;
  }
  return "sel:untitled";
}

export default function ReviewLayer() {
  const { setPendingAnchor, threads } = useReview();
  const [floating, setFloating] = useState(null);
  // Track which anchors have open threads so we can visually flag them.
  const openAnchors = useMemo(() => {
    const s = new Set();
    threads.forEach((t) => {
      if (t.root.status === "open" && t.root.anchor) s.add(t.root.anchor);
    });
    return s;
  }, [threads]);

  // 1) Heading pills + inline anchor highlights ----------------------------
  useEffect(() => {
    // The draft body is rendered by CmsPage AFTER an async fetch, so the
    // <h2>/<h3> nodes we need to attach to may not exist when this effect
    // first runs. Attach a MutationObserver on <main> so pills wire up the
    // moment the body arrives — and again on every re-render of the body.
    const created = new WeakSet();
    const trackedPills = [];

    function wireIn(body) {
      if (!body) return;
      body.querySelectorAll("h2[id], h3[id]").forEach((h) => {
        if (created.has(h)) return;
        created.add(h);
        h.style.position = h.style.position || "relative";
        h.classList.add("group");
        const pill = document.createElement("button");
        pill.type = "button";
        pill.setAttribute("data-testid", `review-heading-pill-${h.id}`);
        pill.setAttribute("aria-label", `Add comment on section ${h.textContent}`);
        pill.className =
          "vv-review-pill absolute -right-2 top-1/2 -translate-y-1/2 translate-x-full " +
          "opacity-0 group-hover:opacity-100 focus:opacity-100 transition text-[10px] uppercase tracking-widest " +
          "bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-2 py-0.5 " +
          "flex items-center gap-1 hover:bg-amber-200";
        pill.innerHTML = '<span>Comment</span>';
        pill.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          setPendingAnchor({
            anchor: headingAnchor(h),
            excerpt: h.textContent.trim().slice(0, 200),
            label: `Section: ${h.textContent.trim()}`,
          });
        });
        h.appendChild(pill);
        trackedPills.push(pill);
      });
    }

    const scan = () => wireIn(document.querySelector('[data-testid="cms-body"]'));
    scan();

    const obs = new MutationObserver(() => scan());
    obs.observe(document.body, { childList: true, subtree: true });

    return () => {
      obs.disconnect();
      trackedPills.forEach((el) => el.remove());
    };
  }, [setPendingAnchor]);

  // 2) Visual highlight ring around headings that have OPEN threads --------
  useEffect(() => {
    const body = document.querySelector('[data-testid="cms-body"]');
    if (!body) return;
    const restore = [];
    body.querySelectorAll("h2[id], h3[id]").forEach((h) => {
      const anchor = headingAnchor(h);
      const hasOpen = openAnchors.has(anchor);
      const prev = h.dataset.vvHadOpenReview;
      if (hasOpen) {
        if (prev !== "1") {
          h.dataset.vvHadOpenReview = "1";
          h.classList.add("vv-has-open-review");
        }
      } else if (prev === "1") {
        h.dataset.vvHadOpenReview = "";
        h.classList.remove("vv-has-open-review");
      }
      restore.push(h);
    });
    return () => {
      restore.forEach((h) => h.classList.remove("vv-has-open-review"));
    };
  }, [openAnchors]);

  // 3) Text selection floating "Comment" button ----------------------------
  useEffect(() => {
    const body = document.querySelector('[data-testid="cms-body"]');
    if (!body) return;

    function onSelectionChange() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setFloating(null);
        return;
      }
      const range = sel.getRangeAt(0);
      // Only react to selections entirely inside the body.
      if (!body.contains(range.commonAncestorContainer)) {
        setFloating(null);
        return;
      }
      const text = String(sel.toString() || "").trim();
      if (text.length < 3) {
        setFloating(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      setFloating({
        top: rect.top + window.scrollY - 40,
        left: rect.left + window.scrollX + rect.width / 2,
        anchor: selectionAnchor(range, body),
        excerpt: text.slice(0, 240),
      });
    }
    function onScrollOrResize() { setFloating(null); }

    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  return floating ? (
    <button
      type="button"
      data-testid="review-selection-comment-btn"
      className="fixed z-[80] -translate-x-1/2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold shadow-lg rounded-full px-3 py-1.5 flex items-center gap-1"
      style={{ top: floating.top, left: floating.left }}
      onMouseDown={(e) => {
        // Preserve the selection when the mousedown fires on the button.
        e.preventDefault();
      }}
      onClick={() => {
        setPendingAnchor({
          anchor: floating.anchor,
          excerpt: floating.excerpt,
          label: "Selected text",
        });
        setFloating(null);
        window.getSelection()?.removeAllRanges();
      }}
    >
      <MessageSquarePlus size={13} /> Comment on selection
    </button>
  ) : null;
}
